/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Heading,
    FlexRow,
    FlexCol,
    TextField,
    ScrollableList,
    NoMapboxGlNotice,
    ScrollableListItem,
    StarRating,
    FauxImage,
    FauxUnavailableImage,
    Icon,
    ContentContainerBodyShift,
    Button,
    useBreakpoint,
} from 'sarsaparilla';
import cloneDeep from 'lodash/cloneDeep';
import filter from 'lodash/filter';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import mapboxgl from 'mapbox-gl';
import BasicMap from 'shared-ui/maps/templates/BasicMap';
import { convertSearchResultToGeoJson } from '../../utils';
import { searchPermitTypes } from '../../api';
import type { ParentLocation, TreeMapItem, TreePermitData } from '../../../types/permit';

interface ItemTypes {
    item: Partial<{
        average_rating: number;
        number_of_ratings: number;
        preview_image_url: string;
        name: string;
        id: string;
        state: any;
    }>;
}
function ListItem({ item }: ItemTypes) {
    const average = get(item, 'average_rating', 0);
    const total = get(item, 'number_of_ratings', 0);
    const { isSmAndDown, isXlAndUp } = useBreakpoint();

    return (
        <div className="tree-map-list-item-wrap">
            <FlexRow hasGutters={false} className="tree-map-list-item">
                {(isSmAndDown || isXlAndUp) && (
                    <FlexCol xs="variable" className="mr-2">
                        {isNil(item?.preview_image_url) ? (
                            <FauxUnavailableImage className="tree-map-list-item-image-unavailable" />
                        ) : (
                            <FauxImage
                                className="tree-map-list-item-image"
                                src={item?.preview_image_url}
                                altText={item?.name}
                            />
                        )}
                    </FlexCol>
                )}

                <FlexCol xs="auto" className="tree-map-list-item-text">
                    <Heading appearance="h6" headingLevel={3}>
                        <Button
                            isUnstyled
                            href={`/tree-permits/${item.id}`}
                            aria-label={`${item.name}`}
                            gaTrackingId="253584760361"
                        >
                            {item.name}
                        </Button>
                    </Heading>
                    <p className="tree-map-list-item-description mb-1">{item.state}</p>
                    <StarRating rating={average} reviewCount={total} />
                </FlexCol>
            </FlexRow>
        </div>
    );
}

ListItem.propTypes = {
    item: PropTypes.any,
};

interface TreeMapTypes {
    locations: Partial<Array<TreePermitData>>;
}

function TreeMap({ locations }: TreeMapTypes) {
    const [filterValue, setFilterValue] = useState('');
    const match = (value: any, key: string) =>
        get(value, key, '').toLowerCase().indexOf(filterValue.toLowerCase()) !== -1;
    const filteredLocations = filter(
        locations,
        (val) => match(val, 'name') || match(val, 'state')
    );

    const [searchLocations, setSearchLocations] = useState<
        Array<Partial<ParentLocation>>
    >([]);

    useEffect(() => {
        searchPermitTypes().then((response: any) => {
            setSearchLocations(response?.data?.results);
        });
    }, []);

    const locationsMetadata = cloneDeep(filteredLocations).map((item) => {
        const newItem: Partial<TreeMapItem> = { ...item };
        const matchedLocation: Partial<TreeMapItem> | undefined = searchLocations.find(
            (location: Partial<ParentLocation>) => {
                return location?.entity_id === item?.id;
            }
        );

        newItem.average_rating = matchedLocation?.average_rating;
        newItem.number_of_ratings = matchedLocation?.number_of_ratings;
        newItem.preview_image_url = matchedLocation?.preview_image_url;

        return newItem;
    });

    const isMapboxGlSupported = mapboxgl.supported();
    const passGeoJsonData = convertSearchResultToGeoJson(locationsMetadata);
    const { isSmAndDown } = useBreakpoint();

    return (
        <ContentContainerBodyShift className="mb-8">
            <FlexRow>
                <FlexCol id="locations" className="tree-permit-tree-map">
                    <div className="tree-permit-tree-map-container">
                        <Heading
                            headingLevel={2}
                            appearance="h4"
                            hasUnderline="left"
                            className="mb-1"
                        >
                            {isSmAndDown ? (
                                <>Search for a Tree Near You</>
                            ) : (
                                <>Explore the Map</>
                            )}
                        </Heading>

                        <p className="mt-1 mb-2">
                            Discover forests near you offering Christmas tree permits for
                            purchase online.
                        </p>
                    </div>
                </FlexCol>
            </FlexRow>

            <FlexRow>
                <FlexCol md={4} lg={4} className="tree-map-list-wrap">
                    <TextField
                        id="map-filter-input"
                        isLabelVisible={false}
                        label=""
                        value={filterValue}
                        onChange={(e) => setFilterValue(e.target.value)}
                        placeholder="Search by name, location or state"
                        iconElement={<Icon iconName="search" />}
                        hasClearButton
                    />
                    <ScrollableList maxHeight={600} className="map-specific-list-results">
                        {!isEmpty(locationsMetadata) &&
                            locationsMetadata.map(
                                (
                                    item: Partial<{
                                        average_rating: number;
                                        number_of_ratings: number;
                                        preview_image_url: string;
                                        name: string;
                                        id: string;
                                        state: any;
                                    }>
                                ) => (
                                    <ScrollableListItem key={item.id}>
                                        <ListItem item={item} />
                                    </ScrollableListItem>
                                )
                            )}
                        {isEmpty(locationsMetadata) && (
                            <p className="map-specific-pass-no-results">
                                No Results Available. Please adjust your search and try
                                again.
                            </p>
                        )}
                    </ScrollableList>
                </FlexCol>

                <FlexCol md={8} lg={8} className="tree-permit-tree-map-wrap">
                    <div className="per-availability-aside">
                        <div className="per-availability-map" id="map">
                            {isMapboxGlSupported && (
                                <div id="Mapbox" className="map">
                                    <BasicMap
                                        center={[-98.585522, 39.8333333]}
                                        mapContainer="Mapbox"
                                        zoom={4}
                                        service="treepermit"
                                        geojson={
                                            isEmpty(locationsMetadata)
                                                ? undefined
                                                : passGeoJsonData
                                        }
                                        checkUSBoundary={true}
                                    />

                                    {!isMapboxGlSupported && <NoMapboxGlNotice />}
                                </div>
                            )}
                        </div>
                    </div>
                </FlexCol>
            </FlexRow>
        </ContentContainerBodyShift>
    );
}

export default TreeMap;

// cSpell:ignore treepermit
